import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";

interface UpgradeToQuarterlyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpgrade: () => void;
  onContinueMonthly: () => void;
}

export function UpgradeToQuarterlyModal({
  open,
  onOpenChange,
  onUpgrade,
  onContinueMonthly,
}: UpgradeToQuarterlyModalProps) {
  const handleDismiss = () => {
    onOpenChange(false);
    onContinueMonthly();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        handleDismiss();
      }
    }}>
      <DialogContent className="sm:max-w-md animate-in fade-in zoom-in-95 duration-300">
        <DialogHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <div className="text-5xl">🎁</div>
          </div>
          <DialogTitle className="text-2xl font-bold text-purple-900">
            Antes de finalizar...
          </DialogTitle>
          <DialogDescription className="text-gray-600 mt-2">
            Você já vai investir em seu ateliê. Veja uma oportunidade ainda melhor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-6">
          {/* Comparativo de Valores */}
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Você já vai investir <span className="font-bold text-gray-900">R$ 39,00</span> hoje.
            </p>
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-300 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-2">
                Com apenas <span className="font-bold text-purple-900">R$ 40,90 a mais</span>, você garante:
              </p>
              <p className="text-lg font-bold text-purple-900">
                3 meses de acesso ao Ateliê Pro
              </p>
            </div>
          </div>

          {/* Benefícios */}
          <div className="space-y-2 bg-gradient-to-b from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-900">Economiza R$ 37,10</p>
                <p className="text-xs text-green-700">comparado ao plano mensal</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-900">Apenas R$ 26,63/mês</p>
                <p className="text-xs text-green-700">valor mensal equivalente</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-900">Promoção de Lançamento</p>
                <p className="text-xs text-green-700">pague 2 meses, use por 3</p>
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="space-y-3 pt-4">
            <Button
              onClick={onUpgrade}
              className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold text-base shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 hover:-translate-y-1"
            >
              <span className="mr-2">💜</span>
              Economizar R$ 37,10
            </Button>

            <Button
              onClick={handleDismiss}
              variant="outline"
              className="w-full h-12 border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all duration-200"
            >
              Continuar com o plano mensal
            </Button>
          </div>

          {/* Aviso discreto */}
          <p className="text-xs text-center text-gray-500 pt-2">
            Você sempre pode cancelar a qualquer momento
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
